import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthApiService, ResearchSurveyApiService } from '@digilize/shared/data/services/src';
import { ErrorKind, LanguageCode, SnackbarType, Token } from '@digilize/shared/definitions/src';
import { AuthService } from '@digilize/shared/feature/modules/auth/src/lib/auth.service';
import { keepAuthTokensForSurvey } from '@digilize/shared/feature/modules/decision-tree/src/lib/survey-session-auth';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { CaptchaService } from '@digilize/shared/utils/services/src';
import { TranslocoService } from '@jsverse/transloco';
import { appConfig } from '@suc/src/environments/app.config';
import { environment } from '@suc/src/environments/environment';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { of, Subscription, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';

@Component({
  selector: 'suc-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  form = new FormGroup({ email: new FormControl('', [Validators.required, Validators.email]) });
  respState: 'none' | 'error' | 'success' | 'expiredCode' | 'link_depleted' | 'link_inactive' = 'none';
  loading = false;
  redirecting = false;
  isMyQuestionnairesRedirectDisabled = false;
  meSub: Subscription;
  redirectionErrorToken;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authApiService: AuthApiService,
    private researchSurveyApiService: ResearchSurveyApiService,
    private authService: AuthService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private captchaService: CaptchaService,
    private snackbarService: SnackbarService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.captchaService.setCaptchaVisibility('visible');
    this.handleLoginCodeParam();
    this.handleTokenParam();

    if (this.authService.isLoggedIn() && !this.isMyQuestionnairesRedirectDisabled) {
      this.router.navigate(['/my-questionnaires']);
    }
  }

  private handleLoginCodeParam() {
    const loginCode = this.route.snapshot.queryParamMap.get('login_code');
    if (loginCode) {
      this.authService.logout(null);
      this.loading = true;
      /*       console.log({loginCode}); */

      this.meSub = of({})
        .pipe(
          switchMap(() => {
            return this.recaptchaV3Service.execute('authWithCode').pipe(
              switchMap((captchaToken) => {
                return this.authApiService.authWithCode(loginCode, this.captchaService.getCaptchaHeaders(captchaToken));
              })
            );
          }),
          retry({
            count: 3,
            delay: (error, count) => {
              if (error.error.error.type === ErrorKind.CAPTCHA_INVALID) {
                return throwError(() => error);
              }
              return timer(Math.min(10000, 2 ^ (count * 1000)));
            },
          }),
          catchError((err) => {
            if (err.error.error.type === ErrorKind.CAPTCHA_INVALID) {
              this.snackbarService.openSnackbar(SnackbarType.Error, {
                duration: 15000,
                data: { msg: 'alert.custom.seems_you_are_bot', translate: true },
              });
              return throwError(err);
            } else {
              this.respState = 'expiredCode';
              this.loading = false;
              return throwError(err);
            }
          }),
          switchMap((resp: Token) => {
            this.authService.setSession(resp);
            return this.authApiService.me;
          })
        )
        .subscribe({
          next: (resp) => {
            this.loading = false;
            if (resp) {
              this.router.navigate(['/my-questionnaires']);
            }
          },
        });
    }
  }

  private handleTokenParam() {
    const token = this.route.snapshot.queryParamMap.get('token');

    if (token) {
      this.loading = true;
      this.redirecting = true;
      this.isMyQuestionnairesRedirectDisabled = true;
      this.authService.logout(null);
      const tokenWithCustomParams = decodeURIComponent(window.location.search);
      this.researchSurveyApiService
        .start(tokenWithCustomParams)
        .pipe(
          retry({
            count: 3,
            delay: (error, count) => {
              if (error?.error?.error?.info?.kind === ErrorKind.INACTIVE) {
                return throwError(() => error);
              }
              if (error?.error?.error?.info?.kind === ErrorKind.LINK_DEPLETED) {
                this.respState = 'expiredCode';
                return throwError(() => error);
              }
              return timer(Math.min(10000, 2 ^ (count * 1000)));
            },
          })
        )
        .subscribe({
          next: (resp) => {
            this.authService.setSession(resp.system_auth);
            keepAuthTokensForSurvey(appConfig.localStorageAuthPrefix, resp.dms_session_id);
            this.loading = false;
            if (environment.surveyStartUrl) {
              window.location.href = `${environment.surveyStartUrl}/${resp.dms_session_id}`;
            }
          },
          error: (error) => {
            this.loading = false;
            this.redirecting = false;
            this.isMyQuestionnairesRedirectDisabled = false;
            if (error?.error?.error?.info?.kind === ErrorKind.INACTIVE) {
              this.respState = 'link_inactive';
            }
            if (error?.error?.error?.info?.kind === ErrorKind.LINK_DEPLETED) {
              this.respState = 'link_depleted';
            } else {
              this.redirectionErrorToken = token;
            }
          },
        });
    }
  }

  handleLoginClick() {
    this.loading = true;
    this.redirectionErrorToken = null;
    this.authApiService
      .authRequest(this.form.controls.email.value, this.translocoService.getActiveLang() as LanguageCode)
      .pipe(
        retry({
          count: 3,
          delay: (error, count) => {
            return timer(Math.min(10000, 2 ^ (count * 1000)));
          },
        })
      )
      .subscribe({
        next: (resp) => {
          this.loading = false;
          this.respState = 'success';
        },
        error: () => {
          this.loading = false;
          this.respState = 'error';
        },
      });
  }

  ngOnDestroy(): void {
    if (this.meSub) this.meSub.unsubscribe();
    this.captchaService.setCaptchaVisibility('hidden');
  }
}
